import type {
  ReqResVariable,
  SimpleABTVariable,
  SimpleVariable,
  DynamicTaskObjectModules,
  DynamicTaskObjectDetails,
  AutomationModulesContext,
} from 'src/components/lib/automation-module-registry/automation-module-registry';
import {useContext, useMemo} from 'react';
import {AutomationModulesRegistryContext} from 'src/components/lib/automation-module-registry/automation-module-registry';


export type SimpleABTPickerVariable = SimpleABTVariable;
export type VariableDataTypeFilter = Array<
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'time'
  | 'email'
  | 'currency'
  | 'phone'
  | 'html'
  | 'text'
  | 'array'
  | '!string'
  | '!number'
  | '!boolean'
  | '!date'
  | '!time'
  | '!email'
  | '!currency'
  | '!phone'
  | '!html'
  | '!text'
  | '!array'
  | '!object'
  | 'object'
>;
export type PrevNodeVariableType = 'abt' | 'reqRes';
export type VariableTypeFilter = Array<'ems' | 'prevNodes'>;
export type UseCaseFilter = 'settings' | 'content' | 'logical' | 'alert';

type GenericObject = {
  // the + here (not well doc'd by flow) makes all object properties covariant
  // meaning that the value of any string-keyed property is allowed to have a more
  // specific type than `mixed` (i.e. string, number fn, etc)
  // (ie. the likely case for all real instances of GenericObject)
  // learn more here https://flow.org/blog/2016/10/04/Property-Variance/
  readonly [key: string]: unknown;
};

export type AutomationModuleRegistry = {
  ems: GenericObject;
  abt?: GenericObject;
  prevNodes?: GenericObject;
};

export type AutomationPickerHookInput = {
  variableTypeFilter?: VariableTypeFilter;
  anchorEntityType?: string | null | undefined;
  triggerEntityType?: string;
  includedVariableDataTypes?: VariableDataTypeFilter;
  excludedVariableDataTypes?: VariableDataTypeFilter;
  useCaseFilter?: UseCaseFilter;
  allowGlobalVariables?: boolean;
  excludeNodeIds?: string[];
};
export type AutomationPickerHookOutput<T extends SimpleVariable> = {
  loadingAutomationVariables: boolean;
  getVariables: () => Array<string>;
  getPreviousNodeVariables: () => Array<{
    nodeId: string;
    nodeLabel: string;
    abtEvents: Array<string>;
  }>;
  getPreviousNodeVariableInfo: (
    nodeId: string,
    id: string,
    moduleKey: string,
  ) => SimpleABTPickerVariable | ReqResVariable | null | undefined;
  getPreviousNodeVariableName: (key: string) => string;
  getVariableInfo: (id: string) => T | null | undefined;
  getAbtPickerVariableInfo: (
    field: string,
  ) => SimpleABTPickerVariable | null | undefined;
  getAbtUtilVariableInfo: (
    arg1: string,
    entityType: string,
    id: string,
  ) => SimpleABTPickerVariable | null | undefined;
  getAbtUtilVariableList: (
    fieldPath: string,
    entityType: string,
  ) => Map<string, SimpleABTPickerVariable> | null | undefined;
  getPreviousNodeModules: (
    arg1: string,
  ) => DynamicTaskObjectModules | null | undefined;
  getPreviousNodeDetails: (
    arg1: string,
  ) => DynamicTaskObjectDetails | null | undefined;
  getPreviousNodeName: (arg1: string) => string;
  getPreviousNodeVariableInfoByKey: (
    arg1: string,
  ) => ReqResVariable | null | undefined | SimpleABTPickerVariable;
};

// Note(Anant): this hook is used to get the automation variables and previous node variables
// Variable Picker will call this hook
export const useAutomationPicker = <T extends SimpleVariable>({
  //consider all types by default
  variableTypeFilter = ['ems', 'prevNodes'],

  triggerEntityType,
  anchorEntityType = null,

  //consider all types included by default
  includedVariableDataTypes = [
    'string',
    'number',
    'boolean',
    'date',
    'email',
    'currency',
    'time',
    'phone',
    'html',
    'text',
    'array',
  ],

  //consider no types excluded by default
  excludedVariableDataTypes = [],

  useCaseFilter = 'content',
  allowGlobalVariables = true,
  excludeNodeIds = [],
}: AutomationPickerHookInput): AutomationPickerHookOutput<T> => {
  const contextValue = useContext<AutomationModulesContext<T>>(
    AutomationModulesRegistryContext,
  );

  const useCaseFilterToPropMap = {
    content: 'show_on_content',
    settings: 'show_on_settings',
    logical: 'show_on_logicalnodes',
    alert: 'show_on_alerts',
  } as const;

  const {
    automationVariables,
    getPreviousNodeInfo,
    isLoading: loadingAutomationVariables,
  } = contextValue;
  const entityTypeForAbt = anchorEntityType ?? triggerEntityType ?? null;
  const variableList = variableTypeFilter.includes('ems')
    ? automationVariables['ems']
    : new Map();
  const abtEventsVariables =
    variableTypeFilter.includes('prevNodes') &&
    automationVariables['allowedActionEvents'] &&
    entityTypeForAbt
      ? automationVariables['allowedActionEvents'][entityTypeForAbt] ??
        new Map()
      : new Map();

  const abtEventsList = useMemo(
    () =>
      Array.from(abtEventsVariables).reduce((acc, [key, value]: [any, any]) => {
        // @ts-expect-error - TS7006 - Parameter 'innerValue' implicitly has an 'any' type. | TS7006 - Parameter 'moduleKey' implicitly has an 'any' type.
        value.forEach((innerValue, moduleKey) => {
          const nodeId = key;
          if (!acc.has(nodeId)) {
            acc.set(nodeId, new Map());
          }
          // @ts-expect-error - TS7006 - Parameter 'variableValue' implicitly has an 'any' type. | TS7006 - Parameter 'variableId' implicitly has an 'any' type.
          innerValue.forEach((variableValue, variableId) => {
            acc
              .get(nodeId)
              ?.set(
                combinedNodeIdEvent(nodeId, variableId, moduleKey),
                variableValue,
              );
          });
          return acc;
        });

        return acc;
      }, new Map()),
    [abtEventsVariables],
  );

  //NOTE:(diwakersurya) picker always gets the req/res variables based on
  // if they are allowed on this node or not.
  const reqResVariables = automationVariables['allowedReqResVariables']
    ? automationVariables['allowedReqResVariables']
    : new Map();

  //we create a grouped map based on the nodeId to match the existing construct of abt variables
  // and to show under the same ui construct in picker.
  const reqResVariableList = useMemo(
    () =>
      Array.from(reqResVariables).reduce((acc, [nodeId, value]: [any, any]) => {
        // @ts-expect-error - TS2345 - Argument of type '([variableId, variableValue]: [any, any]) => Map<any, any>' is not assignable to parameter of type '(value: unknown, index: number, array: unknown[]) => void'.
        Array.from(value).forEach(([variableId, variableValue]: [any, any]) => {
          if (!acc.has(nodeId)) {
            acc.set(nodeId, new Map());
          }
          acc
            .get(nodeId)
            ?.set(
              combinedNodeIdEvent(nodeId, variableId, variableValue.moduleKey),
              variableValue,
            );
          return acc;
        });

        return acc;
      }, new Map()),
    [reqResVariables],
  );

  const previousNodeVariablesMap = useMemo(
    () =>
      [
        ...(abtEventsList ?? new Map()).keys(),
        ...reqResVariableList.keys(),
      ].reduce((acc, key) => {
        const abtVariable = abtEventsList.get(key) ?? new Map();

        const reqResVariable = reqResVariableList.get(key) ?? new Map();

        return new Map([
          ...acc,
          [
            key,
            new Map([
              ...new Map(
                Array.from(abtVariable, ([key, value]: [any, any]) => [
                  key,
                  {...value, prevNodeVariableType: 'abt'},
                ]),
              ),
              ...new Map(
                Array.from(reqResVariable, ([key, value]: [any, any]) => [
                  key,
                  {...value, prevNodeVariableType: 'reqRes'},
                ]),
              ),
            ]),
          ],
        ]);
      }, new Map()),
    [abtEventsList, reqResVariableList],
  );

  const getVariableInfo = (id: string) => {
    return variableList.get(id) ?? null;
  };

  const getPreviousNodeVariableInfo = (
    nodeId: string,
    id: string,
    moduleKey: string,
  ) => {
    // the created identifier is of the form nodeid;eventid;modulekey for abt and reqres variables
    const identifier = combinedNodeIdEvent(nodeId, id, moduleKey);
    const variableMap = previousNodeVariablesMap.get(nodeId);
    const previousNodeVariable = variableMap?.get(identifier) ?? null;
    if (
      previousNodeVariable &&
      previousNodeVariable.selectionLevelPathId !== id
    ) {
      const selectionLevelPathIdentifier = combinedNodeIdEvent(
        nodeId,
        previousNodeVariable.selectionLevelPathId,
        moduleKey,
      );
      const selectionLevelPathVariable =
        variableMap?.get(selectionLevelPathIdentifier) ?? null;
      previousNodeVariable.selectionLevelTitle =
        selectionLevelPathVariable?.title ?? '';
    }
    return previousNodeVariable;
  };
  const getPreviousNodeVariableInfoByKey = (key: string) => {
    let result = null;

    for (const innerMap of previousNodeVariablesMap.values()) {
      const value = innerMap.get(key);
      if (value) {
        result = value;
        break;
      }
    }
    return result;
  };
  const getPreviousNodeVariableName = (key: string): string => {
    const matchingMap = [...previousNodeVariablesMap.values()].find(
      (innerMap) => innerMap.has(key),
    );
    const variable = matchingMap?.get(key);
    return `${variable?.node_name ?? ''} ${variable?.display_name ?? ''}${
      variable?.title ?? ''
    }`;
  };

  const includedDataTypeFilterSet = new Set(includedVariableDataTypes);
  const excludedDataTypeFilterSet = new Set(excludedVariableDataTypes);

  const includedEmsEntityTypesFilterSet = triggerEntityType
    ? new Set([anchorEntityType, triggerEntityType].filter(Boolean))
    : new Set();

  const getVariables = () => {
    const useCaseFilterProp = useCaseFilterToPropMap[useCaseFilter];
    const variables = [...variableList]
      .filter(([_, value]: [any, any]) => {
        const matchesDataTypeFilters =
          includedDataTypeFilterSet.has(value.type) === true &&
          excludedDataTypeFilterSet.has(value.type) === false;
        return (
          value !== null &&
          typeof value === 'object' &&
          value.type !== 'object' &&
          value[useCaseFilterProp] === true &&
          (matchesDataTypeFilters ||
            (allowGlobalVariables && value.is_global_variable === true)) &&
          Boolean(value.emsGroup) &&
          includedEmsEntityTypesFilterSet.has(value.emsGroup)
        );
      })
      .map(([key, _]: [any, any]) => {
        return key;
      });

    return variables;
  };

  const getPreviousNodeName = (outerKey: string) => {
    const previousNodeInfo = getPreviousNodeInfo(outerKey);
    if (!previousNodeInfo) {
      return '';
    }
    const nodeName = previousNodeInfo[outerKey].details.node_name ?? '';
    const displayName = previousNodeInfo[outerKey].details.display_name ?? '';
    return `${nodeName} ${displayName}`;
  };

  const getPreviousNodeVariables = () => {
    const useCaseFilterProp = useCaseFilterToPropMap[useCaseFilter];
    const result: Array<{
      abtEvents: Array<string>;
      nodeId: string;
      nodeLabel: string;
    }> = [];
    // @ts-expect-error - TS7006 - Parameter 'innerMap' implicitly has an 'any' type. | TS7006 - Parameter 'outerKey' implicitly has an 'any' type.
    previousNodeVariablesMap.forEach((innerMap, outerKey) => {
      // Skip any nodes the caller wants excluded (e.g. the active node itself,
      // which is not a "previous" node of itself).
      if (excludeNodeIds.length > 0 && excludeNodeIds.includes(outerKey)) {
        return;
      }
      //TODO:(diwakersurya) consider included and excluded data types as well
      const varParentObject: Record<string, any> = {};
      const res: Array<string> = [];

      // @ts-expect-error - TS7006 - Parameter 'value' implicitly has an 'any' type. | TS7006 - Parameter 'key' implicitly has an 'any' type.
      innerMap.forEach((value, key) => {
        //Note(Anant): this is unused for now as we have commented out -> nonObjectKeysObj.selectionOptions = options; in getVariableIdentifiers function.
        if (value.selectionOptions) {
          const matchingMap = [];
          value.selectionOptions.forEach(
            (selectionOptions: any, selectionOptionKey: string) => {
              if (
                selectionOptions != null &&
                typeof selectionOptions === 'object' &&
                selectionOptions[useCaseFilterProp] === true &&
                includedDataTypeFilterSet.has(selectionOptions.type) === true &&
                excludedDataTypeFilterSet.has(selectionOptions.type) === false
              ) {
                matchingMap.push(selectionOptionKey);
              }
            },
          );
          if (matchingMap.length > 0) {
            res.push(key);
          }
        } else if (
          value != null &&
          typeof value === 'object' &&
          value[useCaseFilterProp] === true &&
          includedDataTypeFilterSet.has(value.type) === true &&
          excludedDataTypeFilterSet.has(value.type) === false
        ) {
          res.push(key);
        }
      });

      varParentObject['nodeId'] = outerKey;
      varParentObject['nodeLabel'] = getPreviousNodeName(outerKey);
      varParentObject['abtEvents'] = [...res];
      // @ts-expect-error - TS2345 - Argument of type 'Record<string, any>' is not assignable to parameter of type '{ abtEvents: string[]; nodeId: string; nodeLabel: string; }'.
      result.push(varParentObject);
    });
    return result;
  };

  const getAbtPickerVariableInfo = (field: string) => {
    return entityTypeForAbt
      ? automationVariables['abt'][entityTypeForAbt]?.pickerVariables.get(field)
      : null;
  };

  const getAbtUtilVariableList = (fieldPath: string, entityType: string) => {
    const utilMapping = entityTypeForAbt
      ? automationVariables['abt'][entityTypeForAbt]?.utilVariables.get(
          entityType,
        )
      : null;
    return utilMapping?.get(fieldPath) ?? new Map();
  };

  const getAbtUtilVariableInfo = (
    fieldPath: string,
    entityType: string,
    id: string,
  ) => {
    const fieldPathMapping = getAbtUtilVariableList(fieldPath, entityType);
    return fieldPathMapping.get(id);
  };

  const getPreviousNodeModules = (nodeId: string) => {
    const previousNodeInfo = getPreviousNodeInfo(nodeId);
    return previousNodeInfo?.[nodeId] ? previousNodeInfo[nodeId].modules : {};
  };

  const getPreviousNodeDetails = (nodeId: string) => {
    const previousNodeInfo = getPreviousNodeInfo(nodeId);
    return previousNodeInfo?.[nodeId].details;
  };

  return {
    getVariables,
    getPreviousNodeVariables,
    getPreviousNodeVariableInfo,
    getPreviousNodeVariableName,
    getPreviousNodeVariableInfoByKey,
    getVariableInfo,
    getAbtPickerVariableInfo,
    getAbtUtilVariableInfo,
    getPreviousNodeModules,
    getPreviousNodeDetails,
    getPreviousNodeName,
    loadingAutomationVariables,
    getAbtUtilVariableList,
  };
};

export const combinedNodeIdEvent = (
  nodeId: string,
  eventId: string,
  moduleKey: string,
): string => {
  return `${nodeId};${eventId};${moduleKey}`;
};

export const splitNodeIdEvent = (
  combinedIdEvent: string,
): {
  nodeId: string;
  event: string;
  moduleKey: string;
} => {
  const [nodeId, event, moduleKey] = combinedIdEvent.split(';');
  return {
    nodeId,
    event,
    moduleKey,
  };
};
